import { FLAG_WATER, RESOURCES, type GameMap, type ResourceKind } from '@sunderfen/shared';
import { audio, rand } from './engine.js';

/** What the world sounds like at a spot: how much of each layer to play. */
export interface Env { trees: number; river: number; lake: number; sea: number; fire: number; height: number; region: string; night: number; open: number }

/**
 * Precomputed per-tile "soundscape" fields (distance to fresh water, the sea, fires;
 * tree density), sampled at the player each frame. Loops crossfade smoothly; birds,
 * crickets, frogs, gulls and crackles are scheduled as one-shots.
 */
export class Ambience {
  private seaDist: Uint8Array; private freshDist: Uint8Array; private lakeTile: Uint8Array; private treeDensity: Uint8Array;
  private fires: [number, number][] = [];
  private loops: Record<string, { gain: GainNode; filter: BiquadFilterNode }> = {};
  private nextBird = 0; private nextCricket = 0; private nextFrog = 0; private nextGull = 0; private nextCrackle = 0; private nextOwl = 20;
  env: Env = { trees: 0, river: 0, lake: 0, sea: 0, fire: 0, height: 1, region: '', night: 0, open: 1 };
  /** 0..1, set by the game from the world's weather (smoothed by the renderer). */
  rain = 0;
  private nextDrip = 0; private nextThunder = 60;

  constructor(private map: GameMap) {
    const W = map.width, H = map.height, N = W * H;
    const water = (t: number) => (map.flags[t] & FLAG_WATER) !== 0;
    // sea = water connected to the bottom edge; everything else wet is fresh water
    const sea = new Uint8Array(N);
    const q: number[] = [];
    for (let x = 0; x < W; x++) { const t = x + (H - 1) * W; if (water(t)) { sea[t] = 1; q.push(t); } }
    while (q.length) { const t = q.pop()!, x = t % W, z = (t / W) | 0; for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) { const X = x + dx, Z = z + dz; if (X < 0 || Z < 0 || X >= W || Z >= H) continue; const n = X + Z * W; if (!sea[n] && water(n) && Z > H * 0.6) { sea[n] = 1; q.push(n); } } }
    const bfs = (seed: (t: number) => boolean) => {
      const d = new Uint8Array(N).fill(255), qq: number[] = [];
      for (let t = 0; t < N; t++) if (seed(t)) { d[t] = 0; qq.push(t); }
      for (let i = 0; i < qq.length; i++) { const t = qq[i], x = t % W, z = (t / W) | 0; if (d[t] >= 14) continue; for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) { const X = x + dx, Z = z + dz; if (X < 0 || Z < 0 || X >= W || Z >= H) continue; const n = X + Z * W; if (d[n] === 255) { d[n] = d[t] + 1; qq.push(n); } } }
      return d;
    };
    this.seaDist = bfs(t => sea[t] === 1);
    this.freshDist = bfs(t => water(t) && !sea[t]);
    this.lakeTile = new Uint8Array(N);
    for (let t = 0; t < N; t++) this.lakeTile[t] = map.regions.find(r => r.id === map.region[t])?.key === 'lake' ? 1 : 0;
    this.treeDensity = new Uint8Array(N);
    for (const o of map.objects) {
      if (o.type === 'station' && (o.kind === 'campfire' || o.kind === 'furnace')) this.fires.push([o.x + 0.5, o.z + 0.5]);
      if (o.type === 'landmark' && o.kind === 'beacon') this.fires.push([o.x + 0.5, o.z + 0.5]);
      if (o.type !== 'resource' || RESOURCES[o.kind as ResourceKind]?.family !== 'tree') continue;
      for (let dz = -4; dz <= 4; dz++) for (let dx = -4; dx <= 4; dx++) if (map.inBounds(o.x + dx, o.z + dz)) { const t = map.idx(o.x + dx, o.z + dz); this.treeDensity[t] = Math.min(255, this.treeDensity[t] + 1); }
    }
    audio.onStart(() => this.startLoops());
  }

  private startLoops() {
    const ctx = audio.ctx!;
    const loop = (name: string, color: 'white' | 'pink' | 'brown', type: BiquadFilterType, freq: number, q = 0.7) => {
      const src = ctx.createBufferSource(); src.buffer = audio.noiseBuffer(color); src.loop = true;
      const filter = ctx.createBiquadFilter(); filter.type = type; filter.frequency.value = freq; filter.Q.value = q;
      const gain = ctx.createGain(); gain.gain.value = 0;
      src.connect(filter); filter.connect(gain); gain.connect(audio.bus('ambient'));
      src.start(0, Math.random() * 2);
      this.loops[name] = { gain, filter };
    };
    loop('wind', 'brown', 'lowpass', 500);
    loop('leaves', 'pink', 'bandpass', 3200, 0.6);
    loop('river', 'pink', 'bandpass', 900, 0.9);
    loop('sea', 'brown', 'lowpass', 420);
    loop('fire', 'brown', 'lowpass', 260);
    loop('rain', 'pink', 'highpass', 1800, 0.5);
    loop('rainlow', 'brown', 'lowpass', 700);
  }

  /** Sample the fields at the player's tile and blend toward them. */
  update(dt: number, x: number, z: number, night: number, time: number) {
    const m = this.map, tx = Math.max(0, Math.min(m.width - 1, Math.floor(x))), tz = Math.max(0, Math.min(m.height - 1, Math.floor(z))), t = m.idx(tx, tz);
    const near = (d: number, r: number) => (d >= r ? 0 : 1 - d / r);
    const fresh = near(this.freshDist[t], 9), lake = this.lakeTile[t] ? fresh : 0;
    let fire = 0;
    for (const [fx, fz] of this.fires) fire = Math.max(fire, near(Math.hypot(fx - x, fz - z), 7));
    const k = 1 - Math.exp(-dt * 2);
    const e = this.env;
    e.trees += (Math.min(1, this.treeDensity[t] / 10) - e.trees) * k;
    e.river += (fresh - lake - e.river) * k; e.lake += (lake - e.lake) * k;
    e.sea += (near(this.seaDist[t], 12) - e.sea) * k;
    e.fire += (fire - e.fire) * k;
    e.height = m.heightAt(x, z); e.night = night;
    e.region = m.regions.find(r => r.id === m.region[t])?.key ?? '';
    e.open = 1 - e.trees;
    if (!audio.ctx || !this.loops.wind) return;
    const now = audio.now, L = this.loops;
    const gust = 0.6 + 0.4 * Math.sin(time * 0.23) * Math.sin(time * 0.071 + 1.3);
    L.wind.gain.gain.setTargetAtTime(0.1 + 0.12 * gust + Math.max(0, e.height - 2) * 0.03, now, 0.5);
    L.wind.filter.frequency.setTargetAtTime(350 + gust * 450, now, 0.8);
    L.leaves.gain.gain.setTargetAtTime(e.trees * 0.05 * gust, now, 0.5);
    L.river.gain.gain.setTargetAtTime(e.river * 0.16 + e.lake * 0.05, now, 0.4);
    L.river.filter.frequency.setTargetAtTime(700 + Math.sin(time * 3.1) * 120 + Math.sin(time * 7.3) * 80, now, 0.05);
    const swell = 0.5 + 0.5 * Math.sin(time * 0.75);
    L.sea.gain.gain.setTargetAtTime(e.sea * (0.12 + 0.2 * swell), now, 0.3);
    L.sea.filter.frequency.setTargetAtTime(280 + swell * 500, now, 0.3);
    L.fire.gain.gain.setTargetAtTime(e.fire * 0.12, now, 0.3);
    L.rain.gain.gain.setTargetAtTime(this.rain * 0.11, now, 0.8);
    L.rainlow.gain.gain.setTargetAtTime(this.rain * 0.08, now, 0.8);
    this.events(time, e);
    if (this.rain > 0.3) {
      if (time > this.nextDrip) { this.nextDrip = time + rand(0.02, 0.12) / this.rain; audio.tone({ bus: 'ambient', gain: rand(0.006, 0.02) * this.rain, freq: rand(1800, 4200), to: rand(900, 1500), dur: 0.03, pan: rand(-0.9, 0.9) }); }
      if (time > this.nextThunder && this.rain > 0.8) { this.nextThunder = time + rand(40, 110); this.thunder(); }
    }
  }

  private events(time: number, e: Env) {
    const day = 1 - e.night;
    if (time > this.nextBird && day > 0.3) {
      this.nextBird = time + rand(0.6, 3.5) / (0.4 + e.trees + e.open * 0.4);
      if (e.region !== 'ashen') this.bird(e.trees > 0.4 && Math.random() < 0.35 ? 'warbler' : Math.random() < 0.5 ? 'whistle' : 'chirp');
    }
    if (time > this.nextGull && e.sea > 0.3 && day > 0.4) { this.nextGull = time + rand(4, 12); this.gull(); }
    if (time > this.nextCricket && e.night > 0.5 && e.region !== 'ashen') { this.nextCricket = time + rand(0.3, 1.4); this.cricket(); }
    if (time > this.nextFrog && (e.region === 'fen' || e.lake > 0.3 || e.river > 0.5) && (e.night > 0.4 || e.region === 'fen')) { this.nextFrog = time + rand(1.5, 5); this.frog(); }
    if (time > this.nextOwl && e.night > 0.8 && e.trees > 0.3) { this.nextOwl = time + rand(18, 40); this.owl(); }
    if (time > this.nextCrackle && e.fire > 0.05) { this.nextCrackle = time + rand(0.05, 0.35); audio.noiseHit({ bus: 'ambient', gain: e.fire * rand(0.08, 0.25), filter: 'highpass', freq: rand(2000, 5000), dur: rand(0.01, 0.04), pan: rand(-0.3, 0.3) }); }
  }

  private bird(kind: 'whistle' | 'chirp' | 'warbler') {
    const pan = rand(-0.8, 0.8), g = rand(0.025, 0.06), base = rand(2400, 4200);
    const v = { bus: 'ambient' as const, pan, reverb: 0.25 };
    if (kind === 'whistle') { audio.tone({ ...v, gain: g, freq: base, to: base * rand(1.2, 1.5), dur: 0.22, attack: 0.03 }); audio.tone({ ...v, gain: g * 0.8, freq: base * 1.3, to: base * 0.9, dur: 0.3, at: 0.3, attack: 0.03 }); }
    else if (kind === 'chirp') { const n = 2 + Math.floor(Math.random() * 4); for (let i = 0; i < n; i++) audio.tone({ ...v, gain: g, freq: base * rand(0.95, 1.1), to: base * 1.5, dur: 0.05, at: i * rand(0.08, 0.12) }); }
    else { for (let i = 0; i < 10; i++) audio.tone({ ...v, gain: g * 0.7, freq: base * (1 + (i % 2) * 0.18), dur: 0.035, at: i * 0.045 }); }
  }
  private gull() { const pan = rand(-0.9, 0.9); for (let i = 0; i < 2 + Math.floor(Math.random() * 2); i++) audio.tone({ bus: 'ambient', pan, gain: 0.04, type: 'sawtooth', freq: rand(1300, 1500), to: rand(800, 950), dur: 0.35, at: i * 0.42, attack: 0.04, filter: { type: 'bandpass', freq: 1800, q: 3 }, reverb: 0.3 }); }
  private cricket() { const pan = rand(-0.9, 0.9), f = rand(4200, 5200); for (let i = 0; i < 3 + Math.floor(Math.random() * 3); i++) audio.tone({ bus: 'ambient', pan, gain: 0.018, freq: f, dur: 0.04, at: i * 0.065 }); }
  private frog() { const pan = rand(-0.8, 0.8), f = rand(110, 170); for (let i = 0; i < 1 + Math.floor(Math.random() * 3); i++) audio.tone({ bus: 'ambient', pan, gain: 0.05, type: 'square', freq: f, to: f * 0.85, dur: 0.16, at: i * 0.22, filter: { type: 'bandpass', freq: 500, q: 5 }, reverb: 0.2 }); }
  private thunder() {
    const pan = rand(-0.6, 0.6);
    audio.noiseHit({ bus: 'ambient', pan, gain: 0.22, color: 'brown', filter: 'lowpass', freq: 220, to: 60, dur: 3.2, attack: 0.3, reverb: 0.6 });
    audio.noiseHit({ bus: 'ambient', pan, gain: 0.08, color: 'brown', filter: 'lowpass', freq: 500, to: 120, dur: 1.4, at: 0.1, reverb: 0.5 });
  }
  private owl() { const pan = rand(-0.7, 0.7); [0, 0.5, 0.75].forEach((at, i) => audio.tone({ bus: 'ambient', pan, gain: 0.05, freq: i === 0 ? 420 : 400, to: 360, dur: i === 0 ? 0.45 : 0.25, at, attack: 0.05, reverb: 0.5 })); }
}
